import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription, SubscriptionTier } from '@/hooks/useSubscription';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Lock, ArrowUpRight } from 'lucide-react';

const TIER_ORDER: Record<string, number> = {
  visitor: 0,
  byod: 1,
  essential: 2,
  'essential-plus': 3,
  pro: 4,
};

const TIER_LABELS: Record<string, Record<string, string>> = {
  en: {
    visitor: 'Visitor',
    byod: 'BYOD',
    essential: 'Essential',
    'essential-plus': 'Essential Plus',
    pro: 'Pro',
  },
  fr: {
    visitor: 'Visiteur',
    byod: 'BYOD',
    essential: 'Essential',
    'essential-plus': 'Essential Plus',
    pro: 'Pro',
  },
};

const translations = {
  en: {
    title: 'Premium Feature',
    description: 'This feature requires the',
    orHigher: 'plan or higher.',
    upgrade: 'Upgrade Now',
  },
  fr: {
    title: 'Fonctionnalité Premium',
    description: 'Cette fonctionnalité nécessite le forfait',
    orHigher: 'ou supérieur.',
    upgrade: 'Mettre à niveau',
  },
};

interface SubscriptionGateProps {
  children: ReactNode;
  requiredTier: SubscriptionTier;
  fallback?: ReactNode;
  silent?: boolean;
  upgradePath?: string;
}

export function SubscriptionGate({
  children,
  requiredTier,
  fallback,
  silent = false,
  upgradePath = '/buyer/upgrade',
}: SubscriptionGateProps) {
  const { tier, loading } = useSubscription();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const lang = (language === 'fr' ? 'fr' : 'en') as 'en' | 'fr';
  const t = translations[lang];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  const userLevel = TIER_ORDER[tier ?? 'visitor'] ?? 0;
  const requiredLevel = TIER_ORDER[requiredTier ?? 'visitor'] ?? 0;

  if (userLevel >= requiredLevel) {
    return <>{children}</>;
  }

  if (silent) return null;
  if (fallback) return <>{fallback}</>;

  const tierLabel = TIER_LABELS[lang]?.[requiredTier ?? 'essential'] ?? requiredTier;

  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-muted/50 p-8 text-center">
      <Lock className="h-10 w-10 text-muted-foreground mb-3" />
      <h3 className="text-lg font-semibold text-foreground mb-1">{t.title}</h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-5">
        {t.description}{' '}
        <span className="font-medium text-primary">{tierLabel}</span>{' '}
        {t.orHigher}
      </p>
      <Button onClick={() => navigate(upgradePath)} className="gap-2">
        <ArrowUpRight className="h-4 w-4" />
        {t.upgrade}
      </Button>
    </div>
  );
}
